import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTeacher } from '../../../../context/TeacherContext';
import apiAxiosInstance from '../../../../utils/axiosInstance';
import { useTeacherStandard } from '../../../../hooks/useTeacherStandard';
import { useFilterStandardsByTeaching } from '../../../../hooks/useFilterStandardsByTeaching';
import { extractTeachingStandardId } from '../../../../utils/mongodb';
import type { ValidationSummary, ValidationWarning } from '../../../../Types/assessment';
import { getAiBackendUrl } from '../../../../constants/api';
import { buildAiAuthHeaders, consumeSSEStream } from '../../../../hooks/useSSEStream';
import { exportElementToPdf, exportHtmlToDocx } from '../../../../utils/exportUtils';
import { GraphData } from '../../../../Types/graph';
import {
  GRADE_LEVELS,
  type GradeLevel,
  type Question,
  type Standard,
} from '../mathWordProblemsTypes';
import {
  createPrintContent,
  parseQuestionsFromContent,
  PRINT_MEDIA_STYLES,
  svgToImageDataUrl,
} from '../mathWordProblemsUtils';

export function useMathProblems() {
  const { teacher } = useTeacher();

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = PRINT_MEDIA_STYLES;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Form states
  const [questionStyle, setQuestionStyle] = useState('StandardizedTest');
  const [showAnswerKey, setShowAnswerKey] = useState(true);
  const [language, setLanguage] = useState<'en' | 'es'>('en');

  // Content states
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [answerKey, setAnswerKey] = useState<string>('');

  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [savedProblemSetId, setSavedProblemSetId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Grade level and standards
  const [gradeLevel, setGradeLevel] = useState('');
  const [standards, setStandards] = useState<Standard[]>([]);
  const [selectedStandards, setSelectedStandards] = useState<string[]>([]);
  const [availableGradeLevels, setAvailableGradeLevels] = useState<string[]>([]);
  const [loadingGradeLevels, setLoadingGradeLevels] = useState(false);
  
  // Strand filter state - initialize to first available Math strand
  const [selectedStrand, setSelectedStrand] = useState<string>('');

  // For the download dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Number of questions
  const [numQuestions, setNumQuestions] = useState<number>(5);
  
  // Question mix state for dynamic question types
  const [questionMix, setQuestionMix] = useState<string>('multiple_choice');

  // Form collapse state
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  // Streaming content
  const [streamingQuestionsContent, setStreamingQuestionsContent] = useState<string>('');
  const [streamingAnswerKeyContent, setStreamingAnswerKeyContent] = useState<string>('');
  
  // Abort controller for generation
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Status display
  const [currentStatus, setCurrentStatus] = useState<string>('');

  // Sprint 2: Validation warnings and summary
  const [validationWarnings, setValidationWarnings] = useState<ValidationWarning[]>([]);
  const [validationSummary, setValidationSummary] = useState<ValidationSummary | null>(null);
  const [retryAttempts, setRetryAttempts] = useState<number>(0);

  // Off-screen capture for print/PDF: render all questions so every graph can be captured
  const [isCapturingGraphs, setIsCapturingGraphs] = useState(false);
  const captureResolveRef = useRef<((map: Map<number, string>) => void) | null>(null);
  const captureContainerRef = useRef<HTMLDivElement | null>(null);

  // Use custom hook to get teacher's standard name
  const { standardName: teachingStandardName, loading: standardLoading, error: standardError } = useTeacherStandard(teacher);
  
  // Helper function to determine actual text style for API calls based on teacher's teaching standard
  const getActualTextStyle = (selectedStyle: string): string => {
    if (!teachingStandardName) return 'STAAR';
    const upperName = teachingStandardName.toUpperCase();
    if (upperName.includes('TEKS') || upperName.includes('TEXAS')) {
      return 'STAAR';
    }
    if (upperName.includes('COMMON CORE') || upperName.includes('CCSS')) {
      return 'SBAC';
    }
    return 'STAAR';
  };
  
  // Filter standards by teacher's teaching standard
  const teachingFilteredStandards = useFilterStandardsByTeaching(standards, teachingStandardName, 'MathWordProblems');
  
  // Further filter to only include Math-related standards (exclude Reading strands)
  const filteredStandards = React.useMemo(() => {
    // List of strands that are NOT math-related (to exclude)
    const nonMathStrands = ['reading', 'writing', 'speaking', 'listening', 'language', 'foundational skills', 'comprehension'];
    
    return teachingFilteredStandards.filter(standard => {
      const strand = (standard.strand || '').toLowerCase();
      
      // Exclude if it's a known non-math strand
      if (nonMathStrands.some(nonMath => strand.includes(nonMath))) {
        return false;
      }
      
      // Include if it's a known math strand
      const mathStrands = ['number', 'algebra', 'geometry', 'measurement', 'data', 'fraction', 'decimal', 'ratio', 'proportion', 'statistics', 'probability', 'operations', 'expressions', 'equations', 'functions'];
      if (mathStrands.some(mathStrand => strand.includes(mathStrand))) {
        return true;
      }
      
      // If strand doesn't match known patterns, check the code or standard text
      const code = (standard.code || '').toLowerCase();
      const standardText = (standard.standard || '').toLowerCase();
      
      // Include if code or standard text suggests it's math
      if (code.includes('math') || standardText.includes('math')) {
        return true;
      }
      
      // Exclude if code suggests it's ELA/Reading
      if (code.includes('ela') || code.includes('rl.') || code.includes('ri.') || code.includes('rf.') || code.includes('w.') || code.includes('sl.') || code.includes('l.')) {
        return false;
      }
      
      // Default: include if we can't determine (might be math)
      return true;
    });
  }, [teachingFilteredStandards]);
  
  // Get available strands from filtered standards (Math-specific)
  const availableStrands = React.useMemo(() => {
    const strandsSet = new Set<string>();
    filteredStandards.forEach(standard => {
      if (standard.strand) {
        strandsSet.add(standard.strand);
      }
    });
    const strandsArray = Array.from(strandsSet).sort();
    
    // Capitalize first letter of each strand for display
    const capitalizedStrands = strandsArray.map(strand => {
      return strand.charAt(0).toUpperCase() + strand.slice(1).toLowerCase();
    });
    
    return capitalizedStrands;
  }, [filteredStandards]);

  // Update selectedStrand when availableStrands changes
  useEffect(() => {
    if (availableStrands.length > 0) {
      // If current selection is not in available strands, select first available
      if (!availableStrands.some(s => s.toLowerCase() === selectedStrand.toLowerCase())) {
        setSelectedStrand(availableStrands[0]);
      }
    }
  }, [availableStrands, selectedStrand]);

  // Filter standards by selected strand
  const strandFilteredStandards = React.useMemo(() => {
    if (!selectedStrand || selectedStrand === 'All Strands') {
      return filteredStandards;
    }
    return filteredStandards.filter(standard => 
      standard.strand?.toLowerCase() === selectedStrand.toLowerCase()
    );
  }, [filteredStandards, selectedStrand]);

  // Fetch Math standards from /api/detailedstandards (subject=Math; gradeLevel required)
  useEffect(() => {
    const fetchStandards = async () => {
      // Only fetch if we have a grade level selected
      if (!gradeLevel) {
        setStandards([]);
        return;
      }

      try {
        const params: { subject: string; gradeLevel: string } = { 
          subject: 'Math',
          gradeLevel: gradeLevel
        };

        const response = await apiAxiosInstance.get('/api/detailedstandards', { params });
        setStandards(response.data || []);
        
        // Clear selected standards when grade level changes since available standards change
        setSelectedStandards([]);
      } catch (err) {
        console.error('Error fetching Math standards:', err);
        setStandards([]);
      }
    };

    fetchStandards();
  }, [gradeLevel]);

  // Initialize grade level from teacher profile
  useEffect(() => {
    if (teacher?.gradeLevel && !gradeLevel) {
      // Handle numeric grades (K, 1-12)
      const grade = teacher.gradeLevel;
      if (GRADE_LEVELS.includes(grade as GradeLevel)) {
        setGradeLevel(grade);
      }
    }
  }, [teacher?.gradeLevel, gradeLevel]);

  // Fetch available grade levels from /api/detailedstandards/grade-levels (Mathematics only)
  useEffect(() => {
    const fetchGradeLevels = async () => {
      setLoadingGradeLevels(true);
      try {
        const params: Record<string, string> = { subject: 'Math' };
        const teachingStandardsId = extractTeachingStandardId(teacher?.teachingStandards);
        if (teachingStandardsId) params.teachingStandards = teachingStandardsId;

        const response = await apiAxiosInstance.get('/api/detailedstandards/grade-levels', { params });
        setAvailableGradeLevels(Array.isArray(response.data) ? response.data : GRADE_LEVELS);
      } catch (err) {
        console.error('Error fetching grade levels:', err);
        setAvailableGradeLevels(GRADE_LEVELS);
      } finally {
        setLoadingGradeLevels(false);
      }
    };

    fetchGradeLevels();
  }, [teacher?.teachingStandards]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedStandards.length === 0) {
      setError('Please select at least one standard.');
      return;
    }
    
    setIsStreaming(true);
    setIsLoading(true);
    setError(null);
    setQuestions([]);
    setAnswerKey('');
    setStreamingQuestionsContent('');
    setStreamingAnswerKeyContent('');
    setCurrentStatus('Generating math word problems...');
    setHasUnsavedChanges(false);
    setSavedProblemSetId(null);
    // Reset validation state
    setValidationWarnings([]);
    setValidationSummary(null);
    setRetryAttempts(0);
    
    // Collapse form when generating
    setIsFormCollapsed(true);

    // Create abort controller
    const controller = new AbortController();
    setAbortController(controller);

    // Declare variables that need to be accessible in finally block
    let questionsContent = '';
    let didSetQuestions = false;

    try {
      // Build standards array with full details
      // Note: detailedstandards API returns 'standard' field (e.g., "5.4(D)") which we use as 'code'
      const standardsForRequest = selectedStandards
        .map(id => strandFilteredStandards.find(s => s._id === id))
        .filter((s): s is Standard => s !== undefined)
        .map(s => ({
          code: s.code || s.standard, // Use 'standard' as code if 'code' is not available
          description: s.description || s.short_description || ''
        }));

      const requestData = {
        gradeLevel,
        strand: selectedStrand,
        standards: standardsForRequest,
        numQuestions,
        language,
        questionStyle: getActualTextStyle(questionStyle),
        questionMix,
        session_id: `math-${Date.now()}`,
        preserve_context: false
      };

      // Call the AI backend to generate math word problems
      let answerKeyContent = '';

      await consumeSSEStream({
        url: `${getAiBackendUrl()}/generate-math-word-problems`,
        headers: buildAiAuthHeaders(),
        body: JSON.stringify(requestData),
        signal: controller.signal,
        onEvent: (data) => {
              if (data.type === 'questions_content') {
                questionsContent += String(data.content ?? '');
                // Try to parse and format the JSON for nice display - showing questions as they complete
                try {
                  // Try to extract complete question objects as they arrive
                  // Look for complete objects within the array, even if array isn't closed yet
                  const completeQuestions: any[] = [];
                  
                  // First, try to parse the complete array if it exists
                  const fullArrayMatch = questionsContent.match(/^\s*\[([\s\S]*)\]\s*$/);
                  if (fullArrayMatch) {
                    try {
                      const parsed = JSON.parse(questionsContent);
                      if (Array.isArray(parsed)) {
                        completeQuestions.push(...parsed);
                      }
                    } catch {
                      // Array exists but isn't valid JSON yet, try partial parsing below
                    }
                  }
                  
                  // If we couldn't parse the full array, try to extract individual complete objects
                  if (completeQuestions.length === 0) {
                    // Match complete JSON objects with all required fields
                    const objectPattern = /\{\s*"question"\s*:\s*"[^"]*(?:\\.[^"]*)*"\s*,\s*"answers"\s*:\s*\[[^\]]*\]\s*,\s*"correctAnswer"\s*:\s*"[^"]*"\s*,\s*"explanation"\s*:\s*"[^"]*(?:\\.[^"]*)*"\s*,\s*"standardReference"\s*:\s*"[^"]*"\s*,\s*"answerType"\s*:\s*"[^"]*"\s*,\s*"bloomsLevel"\s*:\s*"[^"]*"\s*,\s*"graph"\s*:\s*(?:null|\{[^}]*\})\s*\}/g;
                    let match;
                    while ((match = objectPattern.exec(questionsContent)) !== null) {
                      try {
                        const obj = JSON.parse(match[0]);
                        completeQuestions.push(obj);
                      } catch {
                        // Skip malformed objects
                      }
                    }
                  }
                  
                  if (completeQuestions.length > 0) {
                    // Format completed questions as readable markdown
                    const formatted = completeQuestions.map((q: any, i: number) => {
                      let text = `## Question ${i + 1}\n\n`;
                      text += `${q.question}\n\n`;
                      
                      // Note about graph visualization (will render fully in final display)
                      if (q.graph && q.graph.type) {
                        text += `*[📊 Graph visualization will appear below]*\n\n`;
                      }
                      
                      if (q.answers && Array.isArray(q.answers)) {
                        q.answers.forEach((a: string, j: number) => {
                          text += `**${String.fromCharCode(65 + j)}.** ${a}\n\n`;
                        });
                      }
                      return text;
                    }).join('\n');
                    
                    // Add generating indicator if more questions expected
                    const stillGenerating = !questionsContent.trim().endsWith(']');
                    if (stillGenerating) {
                      setStreamingQuestionsContent(formatted + '\n---\n\n*⏳ Generating more questions...*');
                    } else {
                      setStreamingQuestionsContent(formatted);
                    }
                    setCurrentStatus(`Generated ${completeQuestions.length} question${completeQuestions.length > 1 ? 's' : ''}...`);
                  } else {
                    // No complete questions yet, show progress indicator
                    const charCount = questionsContent.length;
                    if (charCount < 50) {
                      setStreamingQuestionsContent('## Generating Questions...\n\n*Starting generation...*');
                    } else if (charCount < 500) {
                      setStreamingQuestionsContent('## Generating Questions...\n\n*Building first question...*\n\n*Please wait...*');
                    } else {
                      setStreamingQuestionsContent('## Generating Questions...\n\n*Crafting question content...*\n\n*Almost there...*');
                    }
                    setCurrentStatus('Generating questions...');
                  }
                } catch {
                  // Fallback: show progress indicator
                  setStreamingQuestionsContent('## Generating Questions...\n\n*Creating grade-appropriate math word problems...*\n\n*Questions will appear as they complete...*');
                  setCurrentStatus('Generating questions...');
                }
              } else if (data.type === 'answer_key_content') {
                answerKeyContent += String(data.content ?? '');
                setStreamingAnswerKeyContent(answerKeyContent);
                setCurrentStatus('Generating answer key...');
              } else if (data.type === 'complete') {
                const payload = data.data as {
                  questions?: Question[];
                  answerKey?: string;
                  validationWarnings?: ValidationWarning[];
                  validationSummary?: ValidationSummary;
                  retryAttempts?: number;
                } | undefined;
                if (payload && Array.isArray(payload.questions) && payload.questions.length > 0) {
                  setQuestions(payload.questions);
                  didSetQuestions = true;
                } else {
                  const fallbackQuestions = parseQuestionsFromContent(questionsContent);
                  if (fallbackQuestions.length > 0) {
                    setQuestions(fallbackQuestions);
                    didSetQuestions = true;
                  }
                }
                if (payload?.answerKey) {
                  setAnswerKey(payload.answerKey);
                }
                if (payload?.validationWarnings) {
                  setValidationWarnings(payload.validationWarnings);
                }
                if (payload?.validationSummary) {
                  setValidationSummary(payload.validationSummary);
                }
                if (payload?.retryAttempts) {
                  setRetryAttempts(payload.retryAttempts);
                }
                setHasUnsavedChanges(true);
                setCurrentStatus('');
              } else if (data.type === 'error') {
                throw new Error(String(data.error));
              } else if (data.type === 'status') {
                setCurrentStatus(String(data.content || data.message || ''));
              }
        },
        onParseError: (parseError) => {
          console.error('Error parsing SSE data:', parseError);
        },
      });
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Generation aborted by user');
        setCurrentStatus('Generation cancelled');
      } else {
        console.error('Error generating math word problems:', err);
        setError(err.message || 'Failed to generate math word problems. Please try again.');
      }
    } finally {
      if (!didSetQuestions && questionsContent) {
        const fallbackQuestions = parseQuestionsFromContent(questionsContent);
        if (fallbackQuestions.length > 0) {
          setQuestions(fallbackQuestions);
        }
      }
      setIsStreaming(false);
      setIsLoading(false);
      setAbortController(null);
      if (!error) {
        setCurrentStatus('');
      }
    }
  };

  // Handle cancel generation
  const handleCancelGeneration = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  const captureAllGraphImages = useCallback((): Promise<Map<number, string>> => {
    return new Promise((resolve) => {
      captureResolveRef.current = resolve;
      setIsCapturingGraphs(true);
    });
  }, []);

  useEffect(() => {
    if (!isCapturingGraphs) return;
    const container = captureContainerRef.current ?? document.getElementById('math-problems-capture');
    if (!container) return;

    const expectedCount = questions.filter((q) => q.graph && q.graph.type).length;
    if (expectedCount === 0) {
      captureResolveRef.current?.(new Map());
      captureResolveRef.current = null;
      setIsCapturingGraphs(false);
      return;
    }

    const run = async () => {
      const initialDelay = 450;
      await new Promise((r) => setTimeout(r, initialDelay));

      let svgs = container.querySelectorAll('svg');
      for (let retry = 0; retry < 3 && svgs.length === 0 && expectedCount > 0; retry++) {
        await new Promise((r) => setTimeout(r, 200));
        svgs = container.querySelectorAll('svg');
      }

      const graphQuestionIndices = questions
        .map((q, idx) => (q.graph && q.graph.type ? idx : -1))
        .filter((idx) => idx >= 0);
      const graphImages = new Map<number, string>();
      for (let i = 0; i < graphQuestionIndices.length && i < svgs.length; i++) {
        const questionIndex = graphQuestionIndices[i];
        const svg = svgs[i] as SVGSVGElement;
        try {
          const dataUrl = await svgToImageDataUrl(svg);
          graphImages.set(questionIndex, dataUrl);
        } catch {
          // skip
        }
      }
      captureResolveRef.current?.(graphImages);
      captureResolveRef.current = null;
      setIsCapturingGraphs(false);
    };
    run();
  }, [isCapturingGraphs, questions]);

  // Handle download menu
  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  // Handle print - capture all graph images from off-screen render, then open print
  const handlePrint = async () => {
    if (questions.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Capture all graph images from off-screen container (every question, not just visible)
    const graphImages = questions.some((q) => q.graph && q.graph.type)
      ? await captureAllGraphImages()
      : new Map<number, string>();

    const content = createPrintContent(questions, answerKey, gradeLevel, 'print', graphImages);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Math Word Problems - Grade ${gradeLevel}</title>
          <meta charset="utf-8">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            body { 
              font-family: 'Roboto', Arial, sans-serif; 
              font-size: 14px; 
              line-height: 1.6; 
              color: #333;
              padding: 0.5in;
            }
            h1, h2, h3, h4, h5, h6 {
              color: #9c27b0;
              font-weight: 700;
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
            h1 { 
              font-size: 1.75rem; 
              text-align: center; 
              margin-bottom: 1em; 
            }
            h2 { 
              font-size: 1.25rem; 
              border-bottom: 1px solid #e0e0e0; 
              padding-bottom: 0.5em; 
            }
            h3 {
              font-size: 1.1rem;
              font-weight: 700;
              margin-top: 1em;
              margin-bottom: 0.5em;
              color: #9c27b0;
            }
            ul, ol {
              margin-left: 1.5em;
              margin-bottom: 0.75em;
              padding-left: 1.5em;
            }
            li {
              margin-bottom: 0.5em;
              line-height: 1.6;
            }
            hr {
              margin: 1.5em 0;
              border: none;
              border-top: 1px solid #e0e0e0;
            }
            .question { 
              margin-bottom: 2em; 
              page-break-inside: avoid;
            }
            .question-text { 
              margin-bottom: 1em; 
              line-height: 1.8; 
              font-weight: normal;
            }
            .question-text p {
              margin: 0 0 0.5em 0;
            }
            .answer-choices { 
              margin-left: 1em; 
              margin-top: 0.5em; 
            }
            .answer-choice { 
              display: flex; 
              margin-bottom: 0.5em; 
              align-items: flex-start; 
              page-break-inside: avoid;
            }
            .answer-letter { 
              font-weight: bold; 
              margin-right: 0.5em; 
              min-width: 1.5em; 
              color: #7b1fa2;
              flex-shrink: 0;
            }
            .answer-text { 
              flex: 1; 
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
            }
            .answer-text p {
              margin: 0;
              display: inline;
            }
            .graph-placeholder {
              text-align: center;
              margin: 1em 0;
              font-style: italic;
              color: #666;
            }
            .answer-key-section {
              page-break-before: always;
              margin-top: 2em;
              padding-top: 1em;
              border-top: 2px solid #9c27b0;
            }
            .answer-key-section p {
              margin: 0 0 0.75em 0;
              line-height: 1.6;
            }
            .answer-key-section ul,
            .answer-key-section ol {
              margin-left: 1.5em;
              margin-bottom: 0.75em;
              padding-left: 1.5em;
            }
            .answer-key-section li {
              margin-bottom: 0.5em;
              line-height: 1.6;
            }
            .answer-key-section h2 {
              margin-top: 0;
            }
            .answer-key-section h3 {
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
            /* KaTeX math rendering styles */
            .katex { 
              font-size: 1.1em !important;
              font-family: 'KaTeX_Main', 'Times New Roman', serif;
            }
            .katex-display { 
              margin: 1em 0; 
              text-align: center;
            }
            /* Ensure math expressions don't break across lines */
            .katex {
              white-space: nowrap;
            }
            /* Inline math should flow with text */
            .question-text .katex,
            .answer-text .katex {
              display: inline-block;
              white-space: normal;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 1em 0;
              page-break-inside: avoid;
            }
            table td, table th {
              padding: 0.5em;
              border: 1px solid #ddd;
            }
            img {
              max-width: 400px;
              max-height: 400px;
              width: auto;
              height: auto;
              display: block;
              margin: 10px auto;
            }
            @media print {
              @page { 
                margin: 0.5in; 
              }
              body { 
                padding: 0;
                font-size: 12pt;
              }
              h1 { 
                margin-top: 0; 
              }
              .question, 
              .answer-choices, 
              .answer-choice { 
                page-break-inside: avoid !important; 
              }
              .answer-key-section {
                page-break-before: always;
              }
              .katex {
                font-size: 1.05em !important;
              }
            }
          </style>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/katex.min.css">
        </head>
        <body>
          ${content}
          <script src="https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/katex.min.js"></script>
          <script src="https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/contrib/auto-render.min.js"></script>
          <script>
            document.addEventListener('DOMContentLoaded', function() {
              // Render all math expressions
              renderMathInElement(document.body, {
                delimiters: [
                  {left: '$$', right: '$$', display: true},
                  {left: '$', right: '$', display: false},
                  {left: '\\[', right: '\\]', display: true},
                  {left: '\\(', right: '\\)', display: false}
                ],
                throwOnError: false,
                strict: false
              });
              
              // Small delay to ensure rendering completes before print
              setTimeout(function() {
                window.print();
              }, 100);
            });
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    handleDownloadClose();
    
    if (questions.length === 0) {
      setError('No content to download.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Capture all graph images from off-screen render (every question, not just visible)
      const graphImages = questions.some((q) => q.graph && q.graph.type)
        ? await captureAllGraphImages()
        : new Map<number, string>();

      const content = createPrintContent(questions, answerKey, gradeLevel, 'pdf', graphImages);

      const element = document.createElement('div');
      element.innerHTML = `
        <style>
          body { 
            font-family: 'Roboto', Arial, sans-serif; 
            font-size: 14px; 
            line-height: 1.6; 
            color: #333;
          }
          h1, h2, h3, h4, h5, h6 {
            color: #9c27b0;
            font-weight: 700;
            margin-top: 1em;
            margin-bottom: 0.5em;
          }
          h1 { 
            font-size: 1.75rem; 
            text-align: center; 
            margin-bottom: 1em; 
          }
          h2 { 
            font-size: 1.25rem; 
            border-bottom: 1px solid #e0e0e0; 
            padding-bottom: 0.5em; 
          }
          .question { 
            margin-bottom: 2em; 
            page-break-inside: avoid;
          }
          .question-text { 
            margin-bottom: 1em; 
            line-height: 1.8; 
          }
          .question-text p {
            margin: 0 0 0.5em 0;
          }
          .answer-choices { 
            margin-left: 1em; 
            margin-top: 0.5em; 
          }
          .answer-choice { 
            display: flex; 
            margin-bottom: 0.5em; 
            align-items: flex-start; 
            page-break-inside: avoid;
          }
          .answer-letter { 
            font-weight: bold; 
            margin-right: 0.5em; 
            min-width: 1.5em; 
            color: #7b1fa2;
            flex-shrink: 0;
          }
          .answer-text { 
            flex: 1; 
            word-wrap: break-word;
            overflow-wrap: break-word;
            max-width: 100%;
          }
          .answer-text p {
            margin: 0;
            display: inline;
          }
            .answer-key-section {
              page-break-before: always;
              margin-top: 2em;
              padding-top: 1em;
              border-top: 2px solid #9c27b0;
            }
            .answer-key-section p {
              margin: 0 0 0.75em 0;
              line-height: 1.6;
            }
            .answer-key-section ul,
            .answer-key-section ol {
              margin-left: 1.5em;
              margin-bottom: 0.75em;
              padding-left: 1.5em;
            }
            .answer-key-section li {
              margin-bottom: 0.5em;
              line-height: 1.6;
            }
            .answer-key-section h2 {
              margin-top: 0;
            }
            .answer-key-section h3 {
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
          .katex { 
            font-size: 1.1em !important;
            font-family: 'KaTeX_Main', 'Times New Roman', serif;
          }
          .katex-display { 
            margin: 1em 0; 
            text-align: center;
          }
          .katex {
            white-space: nowrap;
          }
          .question-text .katex,
          .answer-text .katex {
            display: inline-block;
            white-space: normal;
          }
        </style>
        ${content}
      `;
      element.style.padding = '20px';
      element.className = 'pdf-container';
      document.body.appendChild(element);
      
      // Load and render KaTeX before PDF generation
      const loadKaTeX = (): Promise<void> => {
        return new Promise((resolve) => {
          // Check if KaTeX is already loaded
          // @ts-ignore
          if (window.katex && window.renderMathInElement) {
            // @ts-ignore
            window.renderMathInElement(element, {
              delimiters: [
                {left: '$$', right: '$$', display: true},
                {left: '$', right: '$', display: false},
                {left: '\\[', right: '\\]', display: true},
                {left: '\\(', right: '\\)', display: false}
              ],
              throwOnError: false,
              strict: false
            });
            resolve();
            return;
          }
          
          // Load KaTeX CSS
          const katexCSS = document.createElement('link');
          katexCSS.rel = 'stylesheet';
          katexCSS.href = 'https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/katex.min.css';
          document.head.appendChild(katexCSS);
          
          // Load KaTeX JS
          const katexScript = document.createElement('script');
          katexScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/katex.min.js';
          katexScript.onload = () => {
            const autoRenderScript = document.createElement('script');
            autoRenderScript.src = 'https://cdn.jsdelivr.net/npm/katex@0.16.27/dist/contrib/auto-render.min.js';
            autoRenderScript.onload = () => {
              // @ts-ignore
              if (window.renderMathInElement) {
                // @ts-ignore
                window.renderMathInElement(element, {
                  delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\[', right: '\\]', display: true},
                    {left: '\\(', right: '\\)', display: false}
                  ],
                  throwOnError: false,
                  strict: false
                });
              }
              // Wait a bit for rendering to complete
              setTimeout(resolve, 200);
            };
            document.head.appendChild(autoRenderScript);
          };
          document.head.appendChild(katexScript);
        });
      };

      await loadKaTeX();

      await exportElementToPdf(element, {
        filename: `math-word-problems-grade-${gradeLevel || 'all'}.pdf`,
        margin: 0.7,
        pagebreakBefore: ['.answer-key-section'],
        renderDelayMs: 300,
      });
      
      // Clean up
      document.body.removeChild(element);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Word download
  const handleDownloadWord = async () => {
    handleDownloadClose();
    
    if (questions.length === 0) {
      setError('No content to download.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Capture all graph images from off-screen render (every question, not just visible)
      const graphImages = questions.some((q) => q.graph && q.graph.type)
        ? await captureAllGraphImages()
        : new Map<number, string>();
      
      // Generate content with graph images
      const content = createPrintContent(questions, answerKey, gradeLevel, 'word', graphImages);
      
      const formattedContent = `
        <html xmlns:w="urn:schemas-microsoft-com:office:word"
              xmlns:v="urn:schemas-microsoft-com:vml"
              xmlns:o="urn:schemas-microsoft-com:office:office">
          <head>
            <meta charset="utf-8">
            <style>
              body { 
                font-family: 'Roboto', Arial, sans-serif; 
                font-size: 12pt; 
                line-height: 1.6; 
                color: #333;
                padding: 20px;
              }
              h1, h2, h3, h4, h5, h6 {
                color: #9c27b0;
                font-weight: 700;
                margin-top: 1em;
                margin-bottom: 0.5em;
              }
              h1 { 
                font-size: 1.75rem; 
                text-align: center; 
              }
              h2 { 
                font-size: 1.25rem; 
                border-bottom: 1px solid #e0e0e0; 
                padding-bottom: 0.5em; 
              }
              .question { 
                margin-bottom: 2em; 
              }
              .question-text { 
                margin-bottom: 1em; 
                line-height: 1.8; 
              }
              .answer-choice { 
                margin-bottom: 0.5em; 
              }
              .answer-letter { 
                font-weight: bold; 
                margin-right: 0.5em; 
                min-width: 1.5em; 
                color: #7b1fa2;
              }
              .answer-text { 
                word-wrap: break-word;
                overflow-wrap: break-word;
                max-width: 100%;
              }
              .answer-text p {
                margin: 0;
                display: inline;
              }
              .question-text p {
                margin: 0 0 0.5em 0;
              }
              .answer-key-section p {
                margin: 0 0 0.75em 0;
              }
              /* Preserve LaTeX syntax in Word - will appear as text but can be converted */
              img { 
                max-width: 400px; 
                max-height: 400px; 
                width: auto; 
                height: auto; 
                display: block; 
                margin: 10px auto; 
              }
            </style>
          </head>
          <body>
            ${content}
          </body>
        </html>
      `;

      exportHtmlToDocx(
        formattedContent,
        `math-word-problems-grade-${gradeLevel || 'all'}.docx`,
      );
    } catch (err) {
      console.error('Error generating Word document:', err);
      setError('Failed to generate Word document. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (questions.length === 0) {
      setError('No questions to save.');
      return;
    }

    if (!teacher || !teacher._id) {
      setError('You must be logged in to save.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Prepare the data to save
      const problemSetData = {
        title: `Math Word Problems - Grade ${gradeLevel}`,
        gradeLevel: gradeLevel,
        questions: questions,
        answerKey: answerKey || '',
        standards: selectedStandards.map(id => {
          const standard = strandFilteredStandards.find(s => s._id === id);
          return standard ? {
            code: standard.code || standard.standard,
            description: standard.description || standard.short_description || ''
          } : null;
        }).filter(Boolean),
        strand: selectedStrand,
        numQuestions: numQuestions,
        language: language,
        questionStyle: questionStyle,
        questionMix: questionMix,
        teacherId: teacher._id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Save to backend
      let response;
      if (savedProblemSetId) {
        // Update existing
        response = await apiAxiosInstance.put(
          `/api/math-word-problems/${savedProblemSetId}`,
          problemSetData
        );
      } else {
        // Create new
        response = await apiAxiosInstance.post(
          '/api/math-word-problems',
          problemSetData
        );
      }

      if (response.data && response.data._id) {
        setSavedProblemSetId(response.data._id);
        setHasUnsavedChanges(false);
        setSuccessMessage(savedProblemSetId ? 'Problem set updated successfully!' : 'Problem set saved successfully!');
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      console.error('Error saving math word problems:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save problem set. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const graphCount = React.useMemo(
    () => questions.filter(q => q.graph && (q.graph as GraphData).type).length,
    [questions]
  );

  return {
    teacher,
    questionStyle,
    setQuestionStyle,
    showAnswerKey,
    setShowAnswerKey,
    language,
    setLanguage,
    isStreaming,
    error,
    setError,
    questions,
    isLoading,
    answerKey,
    isSaving,
    showSuccessAlert,
    setShowSuccessAlert,
    successMessage,
    gradeLevel,
    setGradeLevel,
    selectedStandards,
    setSelectedStandards,
    availableGradeLevels,
    loadingGradeLevels,
    selectedStrand,
    setSelectedStrand,
    anchorEl,
    open,
    numQuestions,
    setNumQuestions,
    questionMix,
    setQuestionMix,
    isFormCollapsed,
    setIsFormCollapsed,
    streamingQuestionsContent,
    streamingAnswerKeyContent,
    currentStatus,
    validationWarnings,
    setValidationWarnings,
    validationSummary,
    retryAttempts,
    isCapturingGraphs,
    captureContainerRef,
    standardLoading,
    availableStrands,
    strandFilteredStandards,
    GRADE_LEVELS,
    handleSubmit,
    handleCancelGeneration,
    handleDownloadClick,
    handleDownloadClose,
    handlePrint,
    handleDownloadPdf,
    handleDownloadWord,
    handleSave,
    graphCount,
  };
}
